import { useCallback, useEffect, useState } from 'react'
import Cookies from 'js-cookie'
import { useQuery } from 'react-query'
import { NatsConnection } from 'nats.ws'
import { newrelicCustomEvents } from '@/root/utils/common/custom/newrelic-custom-events'
import { fetchRequest } from '@/root/utils/network-adapter/fetch-request'
import {
  IND_STOCKS_BASEPATH,
  IND_STOCKS_JWT_AUTH,
} from '@/root/api/stocks/apiConstants'

export default function useNatsConnection(
  setConnect: (arg: NatsConnection | undefined) => void,
  platform?: string,
  authorization?: string,
  state?: { reOpenConnectionCount?: number; nats?: NatsConnection },
) {
  const authtoken = authorization ?? Cookies.get('token')
  const [expiry, setExpiry] = useState(0)
  const [reconnection, setReconnection] = useState(false)
  const [isTokenExpired, setTokenExpired] = useState(false)
  const { data, status, isFetching, refetch } = useQuery<{
    success: boolean
    token?: string
    expires_at?: number
  }>(
    [`auth${authtoken}`, state?.reOpenConnectionCount],
    () =>
      fetchRequest({
        config: {
          headers: {
            Authorization: authorization ?? `Bearer ${authtoken}`,
            platform: platform ? platform : 'web',
          },
        },
        timeout: 0,
        url: `${IND_STOCKS_BASEPATH}${IND_STOCKS_JWT_AUTH}${
          reconnection ? `?reconnect=true` : ''
        }`,
      }),
    {
      enabled: !!authtoken,
      retry: 0,
      refetchOnReconnect: 'always',
      onSuccess: data => {
        if (!state?.nats?.isClosed()) {
          void state?.nats?.drain()
        }
        setReconnection(false)
        setExpiry(0)
        if (data.expires_at) {
          const currentUnixTimestamp = Math.floor(Date.now() / 1000)
          const differenceInSeconds = data.expires_at - currentUnixTimestamp
          const differenceInMinutes = differenceInSeconds / 60
          setExpiry(differenceInMinutes)
        }
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        // void init()
      },
      onError: (err?: { statusCode?: number }) => {
        if (err?.statusCode === 512) {
          setTokenExpired(true)
        }
      },
      refetchInterval: expiry ? expiry * 60000 : 0,
    },
  )

  const init = useCallback(async () => {
    try {
      if (status === 'success' && !isFetching) {
        const jwtSecret = data.token
        const { connect, credsAuthenticator } = await import('nats.ws')
        const nc = await connect({
          servers: ['wss://ws.indmoney.com'],
          authenticator: credsAuthenticator(
            new TextEncoder().encode(jwtSecret),
          ),
          reconnect: true,
          maxReconnectAttempts: -1,
          reconnectTimeWait: 2000,
        })

        setConnect(nc)
      }
    } catch (err: unknown) {
      if (
        err !== null &&
        typeof err === 'object' &&
        'code' in err &&
        (err as { code?: string }).code === 'CONNECTION_CLOSED'
      ) {
        void newrelicCustomEvents({
          error: err,
          customErrorType: 'socket-error',
        })
        if (!state?.nats?.isClosed()) {
          void state?.nats?.drain()
        }
        void refetch()
        return
      }
      if (!state?.nats?.isClosed()) {
        void state?.nats?.drain()
      }
      void refetch()

      // Do nothing
      // Socket reconnects if fails, incase it throws an error, no need to display any UI error messages for now, prices will remain static
      console.info('Socket connection failed', err)
    }
  }, [data?.token, refetch, status])

  useEffect(() => {
    const timerId = setInterval(() => {
      if (state?.nats?.isClosed()) {
        setReconnection(true)
        if (!isTokenExpired) {
          void refetch()
        }
      }
    }, 60000)
    return () => {
      clearInterval(timerId)
    }
  }, [refetch, state?.nats, isTokenExpired])

  useEffect(() => {
    void init()
    return () => {
      if (!state?.nats?.isClosed()) {
        state?.nats?.drain()
      }
    }
  }, [status, data?.token, isFetching, refetch])
}
